import React, { useMemo } from 'react';

import { Box } from '@rbilabs/components-library';
import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import useErrorModal from 'hooks/use-error-modal';
import useMediaQuery from 'hooks/use-media-query';
import useOrderNumber from 'hooks/use-order-number';
import { OrderStatus, useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useStoreContext } from 'state/store';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { everyCartEntriesInMenu } from 'utils/cart';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';

import OrderAction from './order-action';
import { RewardText } from './reward-text';
import {
  CateringPendingApproval,
  Col,
  FormattedDate,
  ItemNameContainer,
  Items,
  MoreItems,
  NumItems,
  OrderNumber,
  OrderNumberText,
  OrderNumberWrapper,
  SubtitleContainer,
  Tile,
} from './styled';

import { MODAL_TYPE } from '.';

interface IOrderProps {
  order: IHistoricalOrder;
  handleDisplayDetailsModal: (orderId: string) => void;
  setModalType: (type: string) => void;
}

const Order = ({ order, handleDisplayDetailsModal, setModalType }: IOrderProps) => {
  const {
    reorder: { reorderedOrderId },
  } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { handleReorderClick, disabled } = useReorder(order);
  const { formatMessage, formatList } = useIntl();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('desktop');
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetch Store Failure',
  });

  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [formatMessage, order.createdAt]
  );

  const { itemCSV, numItems, rewardRedeemed } = useMemo(() => {
    const cartEntries = delv(order, 'cart.cartEntries', []);
    const csvForItem = cartEntries.map((entry: IBackendCartEntries) => entry.name).join(', ');
    const totalItemsOrdered = cartEntries.reduce(
      (acc: number, item: IBackendCartEntries) => acc + item.quantity,
      0
    );

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? formatMessage({ id: 'item' })
      : formatMessage({ id: 'items' })
    ).toLowerCase()}`;

    // order redeemed a reward if an entry has `redeemable` as true
    const wasRewardRedeemed = cartEntries.some((entry: IBackendCartEntries) => entry.redeemable);

    return { itemCSV: csvForItem, numItems: itemsCount, rewardRedeemed: wasRewardRedeemed };
  }, [order, formatMessage]);

  const showOrderModal = (type: string) => {
    setModalType(type);
    handleDisplayDetailsModal(order.rbiOrderId);
  };

  const showCateringPendingApproval =
    isCatering(order?.cart.serviceMode) && order?.status === OrderStatus.CATERING_PLACED;

  const handleFinishOrder = async () => {
    const orderStoreId = order.cart?.storeId ? order.cart.storeId.toString() : '';

    // Store needs to be re-fetched if the current one is different is missing.
    if (store.number && store.number !== orderStoreId) {
      try {
        await fetchStore(orderStoreId);
      } catch {
        return openErrorDialog({ message: formatMessage({ id: 'couldNotLocateStore' }) });
      }
    }

    navigate(`${routes.orderConfirmation}/${order.rbiOrderId}`);
  };

  // Show the first item and tell the user how many other items there is in the order
  const itemsArr = itemCSV.split(',');
  const remainingItems = itemsArr.length - 1;
  const renderMoreItems = () => (
    <MoreItems onClick={() => showOrderModal(MODAL_TYPE.SHOW_DETAILS)}>
      {formatMessage({ id: 'moreItems' }, { remainingItems })}
    </MoreItems>
  );
  const createAriaLabel = useCreateOrderAriaLabel();
  const orderNumber = useOrderNumber(order);
  const disableReorder = useMemo(
    () => disabled || !everyCartEntriesInMenu(order?.cart?.reorderCartEntries || []),
    [disabled, order]
  );

  return (
    <Tile data-testid={`recent-order-${order.rbiOrderId}`}>
      <Col>
        {order.cart?.ticketNumber ? (
          <OrderNumberWrapper>
            <OrderNumberText>{`${formatMessage({ id: 'orderNumber' })}:`}</OrderNumberText>
            <OrderNumber>{orderNumber}</OrderNumber>
          </OrderNumberWrapper>
        ) : null}
        <FormattedDate>{formattedDate}</FormattedDate>
        {showCateringPendingApproval && (
          <CateringPendingApproval>
            {formatMessage({ id: 'cateringOrderPendingApproval' })}
          </CateringPendingApproval>
        )}
        <SubtitleContainer>
          <NumItems>{numItems}</NumItems>
        </SubtitleContainer>
        <ItemNameContainer>
          {rewardRedeemed && <RewardText text={formatMessage({ id: 'rewards' })} />}
          <Items>
            {formatList([itemsArr[0], ...(remainingItems > 0 ? [renderMoreItems()] : [])], {
              type: 'unit',
            })}
          </Items>
        </ItemNameContainer>
      </Col>
      <Col>
        <Box flexDirection={isDesktop ? 'row' : 'column'} center width="100%">
          <Box width="100%" minWidth="auto">
            <ActionButton
              data-testid="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              transparent
              fullWidth
              onClick={() => showOrderModal(MODAL_TYPE.SHOW_DETAILS)}
              aria-label={createAriaLabel({
                buttonType: 'view',
                order,
              })}
            >
              {formatMessage({ id: 'viewDetails' })}
            </ActionButton>
          </Box>
          <Box margin={isDesktop ? '0 0 0 1rem' : '1rem 0 0'} width="100%" minWidth="auto">
            <OrderAction
              order={order}
              loading={reorderedOrderId === order.rbiOrderId}
              disabled={disableReorder}
              onTrack={() => showOrderModal(MODAL_TYPE.TRACK_ORDER)}
              onReorder={handleReorderClick}
              onFinish={handleFinishOrder}
            />
          </Box>
        </Box>
      </Col>
      <ErrorDialog />
    </Tile>
  );
};

export default Order;
